import { useMemo } from 'react';
import { Button, Card, Select, Space, Typography } from 'antd';

import TokenInput from '../common/TokenInput';
import LookupSourceField from './LookupSourceField';
import { InputSelect } from '../Input';
import { writableElements } from './AutomationRuleEditor';
import { CLOSE, PLUS } from '../icons';
import { buildInputTokens, buildTriggerTokens } from '../../utils/automationTokens';
import { lookupSourceOptions } from '../../utils/lookupSources';
import { linkElementsTo } from '../../utils/tagRules';
import { normalizeId } from '../../utils/ids';
import {
	CREATE_ITEM,
	KEPT,
	SET_VALUES,
	STEP_LABELS,
	describeKeptStep,
	MAX_STEPS,
	emptyRow,
	newStep,
} from '../../utils/cardActionEditor';

const { Text } = Typography;

const elementLabelOf = el => el?.label?.singular || el?.name;
const isGroup = el => el?.dataType === 'repeating-group';

/**
 * The elements a card action's step may write: what a rule may write
 * (driven, numbered, file and confirmation elements are never offered), and
 * not a repeating group — a group's entries are copied by a rule, and this
 * editor does not build that yet. A stored value on one stays, flagged.
 */
const writable = elements => writableElements(elements).filter(el => !isGroup(el));

/** The last definition of a name wins, as a merged list reads. */
const byName = elements => {
	const map = new Map();
	(elements || []).forEach(el => { if (el?.name) map.set(el.name, el); });
	return map;
};

const NOT_FOUND = ' (not found)';

/**
 * A stored element name no card offers: shown, flagged and kept. A step is
 * never emptied by the card changing under it, and a name is never shown blank.
 */
const withStoredOption = (options, stored) => {
	const name = String(stored || '').trim();
	if (!name || options.some(option => option.value === stored)) return { options, missing: false };
	return { options: [{ value: stored, label: `${stored}${NOT_FOUND}`, disabled: true }, ...options], missing: true };
};

/**
 * The value rows of a step: an element picked by its label and its value. A
 * value is a template built with pills (the `{{ }}` menu inserts what the
 * pressed item holds, today, or what the person typed in when pressing), a
 * lookup is filled from a link or a chosen record, never typed.
 *
 * `blocked` is the sentence for when there is nothing to pick from yet; rows
 * that already hold a name stay on screen, so a stored step is never emptied.
 */
function ValueRows({ rows, onRows, elementOptions, targets, tokens, lookupContext, blocked = null, scopeName = '', ariaPrefix = 'Value' }) {
	const shown = blocked ? rows.filter(row => row.name || row.value) : rows;
	const patch = (key, change) => onRows(rows.map(row => (row.key === key ? { ...row, ...change } : row)));
	return (
		<div className="card-action-rows">
			{blocked ? <Text type="secondary" className="d-block">{blocked}</Text> : null}
			{shown.map(row => {
				const { options, missing } = withStoredOption(elementOptions, row.name);
				const target = row.name ? targets.get(row.name) : null;
				const lookup = target?.dataType === 'card-lookup' ? target : null;
				const label = elementLabelOf(target) || row.name || 'element';
				return (
					<div key={row.key} className="card-action-rows__row">
						<Select
							className="card-action-rows__name"
							value={row.name || undefined}
							placeholder="Element"
							aria-label="Element"
							status={missing ? 'warning' : undefined}
							showSearch
							optionFilterProp="label"
							options={options}
							onChange={value => patch(row.key, { name: value })}
						/>
						<div className="card-action-rows__value">
							{lookup ? (
								<LookupSourceField
									value={row.value}
									onChange={value => patch(row.key, { value })}
									target={lookup}
									sources={lookupContext.sourcesFor(lookup)}
									ctx={lookupContext.ctx}
									collectionId={lookupContext.collectionId}
									ariaLabel={`${ariaPrefix} for ${label}`}
									targetCardName={lookupContext.cardsById?.[normalizeId(lookup?.options?.lookupCardId)]?.name || ''}
								/>
							) : (
								<TokenInput
									value={Array.isArray(row.value) ? row.value.join(', ') : row.value}
									tokens={tokens}
									placeholder="Value"
									ariaLabel={`${ariaPrefix} for ${label}`}
									onChange={value => patch(row.key, { value })}
								/>
							)}
						</div>
						<Button
							type="text"
							danger
							icon={<CLOSE />}
							aria-label="Remove this value"
							onClick={() => onRows(rows.filter(r => r.key !== row.key))}
						/>
						{missing && !blocked ? (
							<Text type="warning" className="card-action-rows__note">
								{`“${row.name}” is not an element a button can set on ${scopeName || 'this card'}. It is kept as it is until you pick another.`}
							</Text>
						) : null}
					</div>
				);
			})}
			{blocked ? null : (
				<Button size="small" icon={<PLUS />} onClick={() => onRows([...rows, emptyRow()])}>Add value</Button>
			)}
		</div>
	);
}

/**
 * The steps of a card action ("Then"). Two are built here: Set values on this
 * item, and Create a related item. Any other step a stored action holds is
 * shown in a sentence and kept as it is.
 *
 * `context`: { collectionId, cardId, cardName, cardsById, getElementsForCard,
 * cardOptions, inputs }. `errors`: step index -> the API's sentence.
 */
export default function CardActionSteps({ steps, onChange, context, errors = {} }) {
	const { collectionId, cardId, cardsById, getElementsForCard, cardOptions, inputs } = context;
	const elementsFor = id => (id && getElementsForCard ? (getElementsForCard(id) || []) : []);
	const triggerElements = useMemo(() => elementsFor(cardId), [cardId, getElementsForCard]); // eslint-disable-line react-hooks/exhaustive-deps
	const triggerAncestors = useMemo(() => {
		const ids = new Set();
		let cursor = cardId;
		while (cursor && !ids.has(cursor)) {
			ids.add(cursor);
			cursor = normalizeId(cardsById?.[cursor]?.parentCardId);
		}
		return ids;
	}, [cardId, cardsById]);

	// The pills: the pressed item, today and now, and what the button asks for.
	const tokens = useMemo(
		() => [...buildInputTokens(inputs), ...buildTriggerTokens(triggerElements, getElementsForCard)],
		[inputs, triggerElements, getElementsForCard]
	);
	const lookupContext = useMemo(() => ({
		collectionId,
		cardsById,
		ctx: { triggerElements, getElementsForCard, inputs },
		sourcesFor: target => lookupSourceOptions({
			target,
			triggerElements,
			triggerCardId: cardId,
			triggerCardName: cardsById?.[cardId]?.name || '',
			inputs,
			getElementsForCard,
			cardsById,
		}),
	}), [collectionId, cardsById, triggerElements, getElementsForCard, inputs, cardId]);

	const patchStep = (key, change) => onChange(steps.map(step => (step.key === key ? { ...step, ...change } : step)));

	const renderSet = step => {
		const options = writable(triggerElements).map(el => ({ value: el.name, label: elementLabelOf(el) }));
		return (<>
			<Text type="secondary" className="d-block">Writes these values onto the item the button is pressed on.</Text>
			<ValueRows
				rows={step.rows}
				onRows={rows => patchStep(step.key, { rows })}
				elementOptions={options}
				targets={byName(writable(triggerElements))}
				tokens={tokens}
				lookupContext={lookupContext}
				scopeName={cardsById?.[cardId]?.name || ''}
				blocked={cardId ? null : 'Choose the card first, then pick which of its elements to set.'}
			/>
		</>);
	};

	const renderCreate = step => {
		const targetElements = elementsFor(step.cardId);
		const options = writable(targetElements).map(el => ({ value: el.name, label: elementLabelOf(el) }));
		const linkOptions = linkElementsTo(targetElements, triggerAncestors).map(el => ({ value: el.name, label: elementLabelOf(el) }));
		const linkMissing = step.linkBackElement && !linkOptions.some(option => option.value === step.linkBackElement);
		// A natural key may use only what this step writes: a key on anything
		// else would match every blank record and suppress every create.
		const written = [...new Set([
			...step.rows.map(row => String(row.name || '').trim()).filter(Boolean),
			...(step.linkBackElement ? [step.linkBackElement] : []),
		])];
		const dedupeOptions = written.map(name => ({ value: name, label: elementLabelOf(targetElements.find(el => el?.name === name)) || name }));
		return (<>
			<InputSelect
				elementName={`cardActionCreateCard-${step.key}`}
				elementLabel="Card to create"
				value={step.cardId || undefined}
				onChange={(n, value) => patchStep(step.key, { cardId: value || '', linkBackElement: '', dedupeOn: [] })}
				options={cardOptions}
				placeholder="Which card is the new item?"
				allowClear={false}
			/>
			<div className="card-action-steps__field">
				<ValueRows
					rows={step.rows}
					onRows={rows => patchStep(step.key, { rows })}
					elementOptions={options}
					targets={byName(writable(targetElements))}
					tokens={tokens}
					lookupContext={lookupContext}
					scopeName={cardsById?.[step.cardId]?.name || ''}
					blocked={step.cardId ? null : 'Pick the card to create first, then choose which of its elements to fill in.'}
				/>
			</div>
			{linkOptions.length || linkMissing ? (
				<div className="card-action-steps__field">
					<label htmlFor={`card-action-link-${step.key}`} className="d-block">Link the new item back to this one</label>
					<Select
						id={`card-action-link-${step.key}`}
						className="card-action-steps__wide"
						allowClear
						value={step.linkBackElement || undefined}
						status={linkMissing ? 'warning' : undefined}
						placeholder="Which link on the new item points here?"
						options={linkMissing ? [{ value: step.linkBackElement, label: `${step.linkBackElement}${NOT_FOUND}`, disabled: true }, ...linkOptions] : linkOptions}
						onChange={value => patchStep(step.key, { linkBackElement: value || '' })}
					/>
					<Text type="secondary" className="d-block">The new item keeps a link to the item the button was pressed on.</Text>
				</div>
			) : null}
			{step.cardId ? (
				<div className="card-action-steps__field">
					<label htmlFor={`card-action-dedupe-${step.key}`} className="d-block">Do not create a duplicate when these match</label>
					<Select
						id={`card-action-dedupe-${step.key}`}
						className="card-action-steps__wide"
						mode="multiple"
						value={step.dedupeOn}
						placeholder="Pick the values that identify one record"
						options={dedupeOptions}
						onChange={value => patchStep(step.key, { dedupeOn: value })}
					/>
					<Text type="secondary" className="d-block">Without this, pressing the button twice creates the item twice.</Text>
				</div>
			) : null}
		</>);
	};

	const addMenu = [SET_VALUES, CREATE_ITEM];
	return (
		<div className="card-action-steps">
			{steps.map((step, index) => (
				<Card
					key={step.key}
					size="small"
					className="card-action-steps__card"
					title={`${index + 1}. ${STEP_LABELS[step.type] || STEP_LABELS[KEPT]}`}
					extra={(
						<Button
							type="text"
							danger
							icon={<CLOSE />}
							aria-label={`Remove step ${index + 1}`}
							onClick={() => onChange(steps.filter(s => s.key !== step.key))}
						/>
					)}
				>
					{step.type === SET_VALUES ? renderSet(step) : null}
					{step.type === CREATE_ITEM ? renderCreate(step) : null}
					{step.type === KEPT ? <Text type="secondary">{describeKeptStep(step.raw, cardsById)}</Text> : null}
					{errors[index] ? <Text type="danger" className="d-block card-action-steps__error" role="alert">{errors[index]}</Text> : null}
				</Card>
			))}
			<Space wrap>
				{addMenu.map(type => (
					<Button
						key={type}
						icon={<PLUS />}
						disabled={steps.length >= MAX_STEPS}
						onClick={() => onChange([...steps, newStep(type)])}
					>
						{STEP_LABELS[type]}
					</Button>
				))}
			</Space>
		</div>
	);
}
